import { ContactMail, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Alert, Box, Chip, ChipOwnProps, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useMessageDialogHandlers } from "../../../App/State/Hooks/MessageDialog/useMessageDialog";
import { useMessageInputState } from "../../../App/State/Hooks/MessageDialog/useMessageInput";
import { useAppSelector } from "../../../App/State/Hooks/reduxHooks";
import {
  ISelectedCandidate,
  selectSelectedCandidateCount,
  selectSelectedCandidatesByType,
} from "../../../App/State/Slices/MessageDialogSlice";

const getContactInfo = (candidate: ISelectedCandidate, type: "email" | "text"): string => {
  return type === "email"
    ? candidate.hasEmail && candidate.emailAddress
      ? candidate.emailAddress
      : "No email info"
    : candidate.hasPhone && candidate.phoneNumber
      ? candidate.phoneNumber
      : "No contact info";
};

export const Recipients = () => {
  const messageState = useMessageInputState();
  const isEmail = messageState.messageType === "email";
  const selectedCandidatesCount = useAppSelector(selectSelectedCandidateCount);
  const selectedTextCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "text"));
  const selectedEmailCandidates = useAppSelector((state) => selectSelectedCandidatesByType(state, "email"));
  const validRecipients = isEmail ? selectedEmailCandidates : selectedTextCandidates;
  const hasValidRecipients = validRecipients.length > 0;
  const [expanded, setExpanded] = useState(false);
  const contactType = isEmail ? "email addresses" : "phone numbers";

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleOnDeselectCandidate = (candidateId: string) => {
    // If deseleting the last candidate, also collapse the expanded view
    if (validRecipients.length === 1 && validRecipients[0].candidateId === candidateId) {
      setExpanded(false);
    }
  };

  return (
    <Alert
      severity={hasValidRecipients ? "info" : "warning"}
      variant="outlined"
      icon={<ContactMail />}
      sx={(theme) => ({
        mb: 4,
        borderColor: hasValidRecipients ? theme.palette.action.disabled : undefined,
        "& .MuiAlert-message": { width: "100%", padding: 0, margin: 0 },
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ width: "100%", py: 1, cursor: hasValidRecipients ? "pointer" : "default" }}
        onClick={hasValidRecipients ? handleExpandClick : undefined}
      >
        <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: hasValidRecipients ? "text.primary" : "text.warning",
              padding: 0,
              margin: 0,
            }}
          >
            Selected Recipients
          </Typography>
          {hasValidRecipients && (
            <IconButton size="small" sx={{ p: 0 }}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Stack>
        {!hasValidRecipients ? (
          <Typography fontStyle="italic">No candidates selected have valid {contactType}</Typography>
        ) : (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "secondary.main",
              ml: "auto",
            }}
          >
            {validRecipients.length} {validRecipients.length === 1 ? "candidate" : "candidates"}
            {selectedCandidatesCount > validRecipients.length ? (
              <em>
                {" "}
                ({selectedCandidatesCount - validRecipients.length} invalid {contactType})
              </em>
            ) : (
              ""
            )}
          </Typography>
        )}
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ py: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <RecipientChipLabels
            candidates={validRecipients}
            type={isEmail ? "email" : "text"}
            onDeselectCandidate={handleOnDeselectCandidate}
          />
        </Box>
      </Collapse>
    </Alert>
  );
};

const RecipientChipLabels = ({
  candidates,
  type,
  onDeselectCandidate,
}: {
  candidates: ISelectedCandidate[];
  type: "email" | "text";
  onDeselectCandidate?: (candidateId: string) => void;
}) => {
  const { handleDeselectCandidate } = useMessageDialogHandlers();
  const defaultProps = {
    variant: "outlined",
    size: "small",
  } as Partial<ChipOwnProps>;

  const handleOnDelete = (candidateId: string) => {
    handleDeselectCandidate(candidateId);
    if (onDeselectCandidate) {
      onDeselectCandidate(candidateId);
    }
  };

  if (candidates.length === 0) {
    return (
      <Chip
        label={`No candidates selected have valid ${type === "email" ? "email addresses" : "phone numbers"}`}
        {...defaultProps}
      />
    );
  }

  if (candidates.length === 1) {
    const candidate = candidates[0];
    return (
      <Chip
        onDelete={() => handleOnDelete(candidate.candidateId)}
        label={
          <small>
            <strong>
              {candidate.firstName} {candidate.lastName.trim()[0]}.
            </strong>{" "}
            - {getContactInfo(candidate, type)}
          </small>
        }
        {...defaultProps}
      />
    );
  }

  if (candidates.length > 100) {
    return <Chip label={<small>{candidates.length} candidates selected</small>} {...defaultProps} />;
  }

  return (
    <>
      {candidates.map((candidate) => (
        <Chip
          key={candidate.candidateId}
          onDelete={() => handleOnDelete(candidate.candidateId)}
          label={
            <small>
              <strong>
                {candidate.firstName} {candidate.lastName.trim()[0]}.
              </strong>{" "}
              - {getContactInfo(candidate, type)}
            </small>
          }
          {...defaultProps}
        />
      ))}
    </>
  );
};
